import { activeMentionQuery, type MentionTarget, matchMentionCandidates, mentionMarkup } from "@serophin/shared";
import type { RefObject } from "preact";
import { useRef, useState } from "preact/hooks";
import { useClickOutside } from "../hooks/useClickOutside";
import { mentionPopupStyle } from "./mentionPopupPosition";

type MentionPopupState = { start: number; query: string; highlightedIndex: number };

type Selection = { start: number; end: number };

const computeNextMentionState = (
  previous: MentionPopupState | null,
  value: string,
  caret: number,
  mentions: MentionTarget[] | undefined,
): MentionPopupState | null => {
  if (!mentions || mentions.length === 0) {
    return null;
  }
  const active = activeMentionQuery(value, caret);
  if (!active) {
    return null;
  }
  return { start: active.start, query: active.query, highlightedIndex: previous && previous.start === active.start ? previous.highlightedIndex : 0 };
};

const advanceHighlight = (state: MentionPopupState, count: number, direction: 1 | -1): MentionPopupState =>
  count === 0 ? state : { ...state, highlightedIndex: (state.highlightedIndex + direction + count) % count };

const buildInsertion = (value: string, state: MentionPopupState, caret: number, target: MentionTarget): { value: string; caret: number } => {
  const insertion = `${mentionMarkup(target.id, target.label)} `;
  return { value: `${value.slice(0, state.start)}${insertion}${value.slice(caret)}`, caret: state.start + insertion.length };
};

export const useMentionPopup = (
  value: string,
  onChange: (value: string) => void,
  mentions: MentionTarget[] | undefined,
  textareaRef: RefObject<HTMLTextAreaElement | null>,
  restoreSelection: (selection: Selection) => void,
) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const [mentionState, setMentionState] = useState<MentionPopupState | null>(null);

  const candidates = mentionState && mentions ? matchMentionCandidates(mentionState.query, mentions) : [];
  const close = () => setMentionState(null);
  const sync = (nextValue: string, caret: number) => setMentionState((previous) => computeNextMentionState(previous, nextValue, caret, mentions));
  const navigate = (direction: 1 | -1) => setMentionState((previous) => (previous ? advanceHighlight(previous, candidates.length, direction) : previous));
  const setHighlightedIndex = (index: number) => setMentionState((previous) => (previous ? { ...previous, highlightedIndex: index } : previous));

  const insertMention = (target: MentionTarget) => {
    if (!mentionState) {
      return;
    }
    const caret = textareaRef.current?.selectionStart ?? mentionState.start + 1 + mentionState.query.length;
    const result = buildInsertion(value, mentionState, caret, target);
    setMentionState(null);
    onChange(result.value);
    requestAnimationFrame(() => restoreSelection({ start: result.caret, end: result.caret }));
  };

  useClickOutside(wrapperRef, close, mentionState !== null);

  const style =
    mentionState && textareaRef.current && wrapperRef.current
      ? mentionPopupStyle(textareaRef.current, wrapperRef.current, mentionState.start + 1 + mentionState.query.length)
      : undefined;

  return { wrapperRef, popupRef, mentionState, candidates, style, sync, navigate, close, insertMention, setHighlightedIndex };
};

export const interceptMentionKeyDown = (
  event: KeyboardEvent,
  mentionState: MentionPopupState | null,
  candidates: MentionTarget[],
  actions: { navigate: (direction: 1 | -1) => void; select: (target: MentionTarget) => void; close: () => void },
): boolean => {
  if (!mentionState) {
    return false;
  }
  if (event.key === "ArrowDown" || event.key === "ArrowUp") {
    event.preventDefault();
    actions.navigate(event.key === "ArrowDown" ? 1 : -1);
    return true;
  }
  if (event.key === "Enter" || event.key === "Tab") {
    event.preventDefault();
    event.stopPropagation();
    const target = candidates[mentionState.highlightedIndex];
    target ? actions.select(target) : actions.close();
    return true;
  }
  if (event.key === "Escape") {
    event.preventDefault();
    event.stopPropagation();
    actions.close();
    return true;
  }
  return false;
};
